import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { StatusItemCard } from './StatusItemCard';
import { type StatusItem, type StatusCategory, CATEGORY_ORDER } from './types';

interface StatusGridProps {
  statusItems: StatusItem[];
  onItemClick: (item: StatusItem) => void;
}

/**
 * StatusGrid, v2 list treatment.
 *
 * Sits inside the "Tracked items" disclosure on the landing. Items render as
 * a flat vertical list (not a card grid, not category-grouped headers) so the
 * section reads as calm supplementary info rather than another competing UI.
 * Items are still sorted by category under the hood so related items stay
 * adjacent.
 */
export function StatusGrid({ statusItems, onItemClick }: StatusGridProps) {
  const { t } = useTranslation('visitor');

  // Sort items by category order, then by their in-category position
  const ordered = useMemo(() => {
    const index = new Map<StatusCategory, number>();
    CATEGORY_ORDER.forEach((c, i) => index.set(c, i));
    return [...statusItems].sort((a, b) => {
      const ai = index.get(a.statusType.category as StatusCategory) ?? 99;
      const bi = index.get(b.statusType.category as StatusCategory) ?? 99;
      return ai - bi;
    });
  }, [statusItems]);

  if (ordered.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F3F4F0]">
          <Sparkles className="h-5 w-5 text-[#5A6472]" />
        </div>
        <p className="text-[15px] font-medium text-[#0A0E14]">{t('status.noItemsToCheck')}</p>
        <p className="mt-1 text-[15px] text-[#5A6472]">{t('status.noItemsDescription')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {ordered.map((item) => (
        <StatusItemCard key={item.id} item={item} onClick={() => onItemClick(item)} />
      ))}
    </div>
  );
}
